import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { Breadcrumbs, CtaBanner, RelatedLinks } from "@/pages/seo/SeoLanding";
import { BERUFE } from "@/data/berufe";
import { SITUATIONEN } from "@/data/situationen";
import { FileText, Rocket } from "lucide-react";

export default function BerufeIndex() {
    const kategorien = [...new Set(BERUFE.map((b) => b.kategorie))];
    const crumbs = [{ name: "Start", path: "/" }, { name: "Berufe", path: "/berufe" }];
    return (
        <PublicLayout>
            <Seo
                title="Bewerbung & Lebenslauf nach Beruf – Muster & Tipps | Bewerbung24.ai"
                description={`Bewerbungs- und Lebenslauf-Ratgeber für ${BERUFE.length}+ Berufe: Aufgaben, Skills, Formulierungen und Muster — plus KI-Generator für deine fertige Bewerbung.`}
                path="/berufe"
                jsonLd={breadcrumbJsonLd(crumbs)}
            />
            <section className="max-w-5xl mx-auto px-6 lg:px-8 py-14" data-testid="berufe-index">
                <Breadcrumbs items={crumbs} />
                <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mt-6">Bewerbung & Lebenslauf nach Beruf</h1>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
                    Jeder Beruf hat eigene Anforderungen — und Personaler erkennen sofort, ob eine Bewerbung dazu passt.
                    Hier findest du für jeden Beruf die typischen Aufgaben, gefragte Skills und konkrete Formulierungen. Und mit einem Klick erstellt die KI daraus deine fertige Bewerbung.
                </p>
                {kategorien.map((kat) => (
                    <div key={kat} className="mt-10">
                        <h2 className="font-heading text-2xl font-bold text-slate-900">{kat}</h2>
                        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {BERUFE.filter((b) => b.kategorie === kat).map((b) => (
                                <div key={b.slug} className="card-elev p-4" data-testid={`beruf-card-${b.slug}`}>
                                    <p className="font-heading font-bold text-slate-900 text-sm">{b.name}</p>
                                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                                        <Link to={`/bewerbung/${b.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-red-50 text-red-700 border border-red-100 hover:bg-red-100">
                                            <Rocket size={10} /> Bewerbung
                                        </Link>
                                        <Link to={`/lebenslauf/${b.slug}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100">
                                            <FileText size={10} /> Lebenslauf
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <div className="mt-12">
                    <h2 className="font-heading text-2xl font-bold text-slate-900">Bewerbung nach Situation</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {SITUATIONEN.map((s) => (
                            <Link key={s.slug} to={`/bewerbung/${s.slug}`} className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-slate-200 hover:border-red-300 hover:text-red-600 text-slate-700">
                                {s.name}
                            </Link>
                        ))}
                    </div>
                </div>
                <CtaBanner />
                <RelatedLinks links={[
                    { name: "Lebenslauf erstellen", path: "/lebenslauf-erstellen" },
                    { name: "Bewerbung erstellen", path: "/bewerbung-erstellen" },
                    { name: "Anschreiben erstellen", path: "/anschreiben-erstellen" },
                    { name: "KI-Bewerbung", path: "/ki-bewerbung" },
                    { name: "Ratgeber", path: "/ratgeber" },
                ]} />
            </section>
        </PublicLayout>
    );
}
